import { ArrowUpRight } from 'lucide-react';
import { ReactNode, useState } from 'react';

interface CTAButtonProps {
  children: ReactNode;
  showArrow?: boolean;
  href?: string;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
}

export default function CTAButton({
  children,
  showArrow = false,
  href,
  onClick,
  className = '',
  disabled = false,
}: CTAButtonProps) {
  const [isHovered, setIsHovered] = useState(false);
  const baseClasses = 'inline-flex items-center border-2 border-red-500 px-[1.5rem] py-[0.75rem] rounded-full font-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 transition-transform duration-1000 ease-out ';
  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : '';
  const combinedClasses = `${baseClasses} ${disabledClasses} ${className}`;

  // Handle touch/click for mobile hover effect
  const handleTouchStart = () => {
    if (!disabled) {
      setIsHovered(true);
    }
  };

  const handleTouchEnd = () => {
    // Keep the hover state active briefly after touch
    setTimeout(() => {
      setIsHovered(false);
    }, 300);
  };

  const content = (
    <>
      <div className="relative overflow-hidden px-2">
        <span 
          className={`block transition-transform duration-300 ease-out body-sm ${
            isHovered ? '-translate-y-full' : 'translate-y-0'
          }`}
        >
          {children}
        </span>
        <span 
          className={`absolute inset-0 flex items-center justify-center transition-transform duration-300 ease-out body-sm ${
            isHovered ? 'translate-y-0' : 'translate-y-full'
          }`}
        >
          {children}
        </span>
      </div>
      {showArrow && (
        <span 
          className="inline-block transition-transform duration-300 ease-out"
          style={{
            transform: isHovered ? 'translateX(8px)' : 'translateX(0px)'
          }}
        >
          <ArrowUpRight 
            className="w-7 h-7 transition-transform duration-300 ease-out"
            style={{
              transform: isHovered ? 'rotate(45deg)' : 'rotate(0deg)'
            }}
            strokeWidth={1}
          />
        </span>
      )}
    </>
  );

  if (href) {
    return (
      <a 
        href={href} 
        className={combinedClasses}
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        {...(showArrow && {
          target: '_blank',
          rel: 'noopener noreferrer',
        })}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={combinedClasses}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {content}
    </button>
  );
}